import React, { useState } from 'react';
import { AlertTriangle, FileDown, X, Hammer } from 'lucide-react';
import { PixelButton } from './PixelButton';
import { PixelBadge } from './PixelBadge';
import { AnalyticsService } from '../../services/analytics';
import userData from '../../data/user_data.json';
import type { DevelopmentWarningConfig } from '../../types/portfolio';

interface DevelopmentWarningBannerProps {
  config: DevelopmentWarningConfig;
}

export const DevelopmentWarningBanner: React.FC<DevelopmentWarningBannerProps> = ({ config }) => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    return sessionStorage.getItem('dismissed_dev_warning') === 'true';
  });

  if (dismissed || !config.enabled) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('dismissed_dev_warning', 'true');
    AnalyticsService.trackCtaClick('DISMISS_DEV_WARNING', 'warning_banner');
  };

  const handleResumeDownload = () => {
    AnalyticsService.trackCtaClick('WARNING_BANNER_DOWNLOAD_CV', userData.cvAsset);
    const link = document.createElement('a');
    link.href = userData.cvAsset;
    link.download = `${userData.name.replace(/\s+/g, '_')}_Resume.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <aside
      aria-label="Development Notice"
      className="w-full bg-pixel-surface-bright border-2 border-pixel-accent p-4 sm:p-5 shadow-pixel-md flex flex-col gap-3 relative transition-all"
    >
      {/* Top Banner Meta Bar */}
      <div className="flex items-center justify-between gap-2 border-b border-pixel-border pb-2.5">
        <div className="flex items-center gap-2">
          <PixelBadge variant="accent" size="sm" pulse>
            {config.badge || 'NOTICE // ACTIVE DEVELOPMENT'}
          </PixelBadge>
          <span className="hidden sm:inline font-code text-[11px] text-pixel-text-muted font-bold">
            // BUILD IN PROGRESS
          </span>
        </div>

        {config.dismissible && (
          <button
            onClick={handleDismiss}
            aria-label="Dismiss warning"
            title="Dismiss notice for this session"
            className="p-1 text-pixel-text-muted hover:text-pixel-accent hover:bg-pixel-surface border border-pixel-border pixel-press transition-colors shadow-pixel-xs flex items-center gap-1 font-arcade text-[9px]"
          >
            <span className="hidden sm:inline">[DISMISS]</span>
            <X size={12} />
          </button>
        )}
      </div>

      {/* Main Banner Message & Action */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-1">
        <div className="flex items-start gap-3.5">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-pixel-surface border-2 border-pixel-accent flex items-center justify-center text-pixel-accent shrink-0 shadow-pixel-xs">
            <Hammer size={18} className="animate-pulse" />
          </div>

          <div className="flex flex-col gap-1">
            <h2 className="font-arcade text-xs sm:text-sm text-pixel-accent leading-relaxed flex items-center gap-2">
              <AlertTriangle size={14} className="inline text-pixel-accent" />
              {config.title || 'THIS PORTFOLIO IS CURRENTLY UNDER ACTIVE DEVELOPMENT'}
            </h2>
            <p className="font-code text-xs sm:text-[13px] text-pixel-text leading-relaxed">
              {config.message ||
                'Some interactive features, devtools, and project showcases are undergoing final polishing. For verified commercial credentials and a full engineering track record, please download the official CV (PDF) below.'}
            </p>
          </div>
        </div>

        {/* Primary CTA Button */}
        <div className="shrink-0 flex items-center self-start lg:self-center pl-12 lg:pl-0">
          <PixelButton
            variant="amber"
            size="md"
            icon={<FileDown size={16} />}
            onClick={handleResumeDownload}
            className="font-arcade text-xs whitespace-nowrap shadow-pixel-sm"
          >
            {config.ctaText || '[DOWNLOAD OFFICIAL CV (PDF)]'}
          </PixelButton>
        </div>
      </div>
    </aside>
  );
};
